/**
 * State of the configurator: what the buyer picked (product, evaluation, type, size, reward share, model, the one bonus,
 * view) and what the API says about it — the catalog (with each program's instrument count) and the quote of the
 * configuration (its effective rules). Choices that a newly picked type or size does not offer fall back to its defaults.
 */
import type { ProgramRules } from "@prop/contracts";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { get, post } from "../../../lib/api";
import {
  type CatalogProgram,
  type Choices,
  type Evaluation,
  evaluations,
  type Family,
  familiesOf,
  nearest,
  type Price,
  type Product,
  priceOf,
  products,
  resolveChoices,
  sizeOf,
  type OfferKey,
  pickOffer,
} from "./model";

type Quote = { choices: Choices; priceUsd: string | null; listPriceUsd: string | null; activationFeeUsd: string | null; activationFeeListUsd: string | null; rules: ProgramRules; rulesHash: string };

export const CATALOG_KEY = ["catalog", "all"] as const;
export const useCatalog = () => useQuery({ queryKey: CATALOG_KEY, queryFn: () => get<{ programs: CatalogProgram[] }>("/programs"), staleTime: 60_000 });

/** The type the configurator opens on: the one that sells the model choice on the most sizes (the classic flagship), else the first. */
const withModel = (f: Family) => f.sizes.filter((p) => p.options?.some((a) => a.key === "model")).length;
const flagship = (fams: Family[]) => fams.reduce<Family | undefined>((best, f) => (!best || withModel(f) > withModel(best) ? f : best), undefined);
const RECOMMENDED = 100_000;

export type Config = ReturnType<typeof useConfigurator>;

export function useConfigurator(initial?: { product?: Product; evaluation?: Evaluation }) {
  const catalog = useCatalog();
  const all = catalog.data?.programs ?? [];

  const [product, setProduct] = useState<Product>(initial?.product ?? "markets");
  const [evaluation, setEvaluation] = useState<Evaluation>(initial?.evaluation ?? "1step");
  const [family, setFamily] = useState<string | null>(null);
  const [size, setSize] = useState<number>(RECOMMENDED);
  const [prefs, setPrefs] = useState<Choices>({});
  const [view, setView] = useState<"cards" | "table">("cards");
  const [offerPick, setOffer] = useState<OfferKey | null>(null);

  const productList = useMemo(() => products(all), [all]);
  const prod = productList.includes(product) ? product : (productList[0] ?? product);
  const evalList = useMemo(() => evaluations(all, prod), [all, prod]);
  const ev = evalList.includes(evaluation) ? evaluation : (evalList.includes("1step") ? "1step" : (evalList[0] ?? evaluation));
  const group = useMemo(() => familiesOf(all, prod, ev), [all, prod, ev]);
  const fam = group.find((f) => f.family === family) ?? flagship(group);
  const program = fam ? nearest(fam, size) : undefined;
  const choices = program ? resolveChoices(program, prefs) : {};
  const local: Price | null = program ? priceOf(program, choices) : null;

  const isDefault = !program?.defaultChoices || Object.entries(choices).every(([k, v]) => program.defaultChoices?.[k] === v);
  const quote = useQuery({
    queryKey: ["quote", program?.versionId, JSON.stringify(choices)],
    queryFn: () => post<Quote>("/programs/quote", { versionId: program?.versionId, options: choices }),
    enabled: !!program && !isDefault,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const rules: ProgramRules | undefined = program ? (isDefault ? (program.effectiveRules ?? program.rules) : (quote.data?.rules ?? program.effectiveRules ?? program.rules)) : undefined;
  const price: Price | null = !isDefault && quote.data ? { priceUsd: quote.data.priceUsd, listPriceUsd: quote.data.listPriceUsd, activationFeeUsd: quote.data.activationFeeUsd, activationFeeListUsd: quote.data.activationFeeListUsd } : local;

  return {
    loading: catalog.isPending,
    error: catalog.isError,
    products: productList,
    product: prod,
    setProduct: (p: Product) => {
      setProduct(p);
      setFamily(null);
    },
    evaluations: evalList,
    evaluation: ev,
    setEvaluation: (e: Evaluation) => {
      setEvaluation(e);
      setFamily(null);
    },
    group,
    family: fam,
    setFamily: (f: Family) => setFamily(f.family),
    size: program ? sizeOf(program) : size,
    setSize,
    recommended: RECOMMENDED,
    prefs,
    setPref: (key: string, value: string) => setPrefs((p) => ({ ...p, [key]: value })),
    view,
    setView,
    program,
    offer: pickOffer(program?.offers, offerPick),
    setOffer,
    choices,
    rules,
    price,
  };
}
